import {
  LanguageModelV2Prompt,
  LanguageModelV2ToolResultPart,
  UnsupportedFunctionalityError,
} from '@ai-sdk/provider';
import { convertUint8ArrayToBase64 } from '@ai-sdk/provider-utils';

export function convertToCloudCodeMessages(
  prompt: LanguageModelV2Prompt,
): { contents: CloudCodeContent[]; systemInstruction?: CloudCodeSystemInstruction } {
  const messages: CloudCodeContent[] = [];
  
  // Cloud Code API expects messages in a specific format
  // System messages should be handled separately
  let systemInstruction: string | undefined;
  
  for (const message of prompt) {
    const { role, content } = message as { role: 'system' | 'user' | 'assistant' | 'tool'; content: any };

    switch (role) {
      case 'system': {
        // Collect system messages
        systemInstruction = systemInstruction 
          ? `${systemInstruction}\n\n${content}`
          : content;
        break;
      }

      case 'user': {
        const parts: CloudCodePart[] = [];
        
        for (const part of content as Array<{ type: string; [k: string]: unknown }>) {
          switch (part.type) {
            case 'text': {
              const p = part as unknown as { text: string };
              parts.push({ text: p.text });
              break;
            }
            case 'file': {
              const file = part as unknown as { data: string | Uint8Array | URL; mediaType: string };
              if (file.data instanceof URL) {
                // Cloud Code base format doesn't support URL media directly here
                throw new UnsupportedFunctionalityError({
                  functionality: 'File URL parts in messages',
                });
              }
              parts.push({
                inlineData: {
                  mimeType: file.mediaType ?? 'application/octet-stream',
                  data: typeof file.data === 'string'
                    ? file.data
                    : convertUint8ArrayToBase64(file.data as Uint8Array),
                },
              });
              break;
            }
          }
        }
        
        messages.push({
          role: 'user',
          parts,
        });
        break;
      }

      case 'assistant': {
        const parts: CloudCodePart[] = [];
        
        for (const part of content as Array<{ type: string; [k: string]: unknown }>) {
          switch (part.type) {
            case 'text': {
              const text = (part as unknown as { text: string }).text;
              if (text.length > 0) {
                parts.push({ text });
              }
              break;
            }
            case 'tool-call': {
              const toolCall = part as unknown as { toolName: string; input: unknown };
              parts.push({
                functionCall: {
                  name: toolCall.toolName,
                  args: toolCall.input,
                },
              });
              break;
            }
          }
        }
        
        if (parts.length > 0) {
          messages.push({
            role: 'model',
            parts,
          });
        }
        break;
      }

      case 'tool': {
        const parts: CloudCodePart[] = [];
        
        for (const toolResponse of content as Array<LanguageModelV2ToolResultPart>) {
          // Map v2 tool-result output/result to Cloud Code response
          let response: any;
          if ('output' in toolResponse) {
            const out = toolResponse.output;
            switch (out.type) {
              case 'text':
                response = out.value;
                break;
              case 'json':
                response = out.value;
                break;
              case 'error-text':
                response = { error: out.value };
                break;
              case 'error-json':
                response = { error: out.value };
                break;
              case 'content':
                response = out.value.map((v: { type: 'text' | 'media'; text?: string; mediaType?: string; data?: string }) =>
                  v.type === 'text'
                    ? { text: v.text as string }
                    : { inlineData: { mimeType: v.mediaType as string, data: v.data as string } },
                );
                break;
              default:
                response = out as any;
            }
          } else if ('result' in toolResponse) {
            // Back-compat if present
            response = (toolResponse as any).result;
          }
          parts.push({
            functionResponse: {
              name: toolResponse.toolName,
              response,
            },
          });
        }
        
        messages.push({
          role: 'function',
          parts,
        });
        break;
      }

      default: {
        const _exhaustiveCheck: never = role;
        throw new Error(`Unsupported role: ${_exhaustiveCheck}`);
      }
    }
  }

  const result = { contents: messages } as { contents: CloudCodeContent[]; systemInstruction?: CloudCodeSystemInstruction };
  if (systemInstruction) {
    result.systemInstruction = {
      role: 'user',
      parts: [{ text: systemInstruction }],
    };
  }

  return result;
}

interface CloudCodeContent {
  role: 'user' | 'model' | 'function';
  parts: CloudCodePart[];
}

interface CloudCodePart {
  text?: string;
  inlineData?: {
    mimeType: string;
    data?: string;
  };
  functionCall?: {
    name: string;
    args?: any;
  };
  functionResponse?: {
    name: string;
    response: any;
  };
}

interface CloudCodeSystemInstruction {
  role: 'user';
  parts: Array<{ text: string }>;
}